import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { IDLE, PENDING, LIST, EDIT } from "const"
import { mtPermissionsRoute } from "client/parts/routes"
import {
    replaceHistoryState,
    hasAccess,
    StateType,
} from "client/utils"
import {
    PRM_PERMISSIONS,
    StoreType,
    AccountPermissionsProps,
    AccountUserList,

    LoadAccountPermissions,
    LoadAccountUsers,
    LoadAccountGroups,
    LoadProjectGroups,
    SaveAccountPermissions,

    setAccountPermissions,
    setAccountUserList,
    setGroupList,
    setAccountPermissionsReloadList,
    setProjectGroupList,
    initPackageSettings,
} from "client/store"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuButton,
    Tab,
    Loader,
    PageForm,
    Button,
    SpinnerIcon,
} from "client/components"

import AccessDenied from "client/parts/accessDenied"
import AccountPermissions from "./account"
import ProjectPermissions from "./project"

let isMounted: boolean

const ACCOUNT = "#account"
const PROJECT = "#project"
const menu = [ACCOUNT, PROJECT]

const MtPermissions = ({
    accountId = null,
    hash = null,
}: AccountPermissionsProps) => {
    const packageList = useSelector((state: StoreType) => state.package.packageList)
    const accountPackageList = useSelector((state: StoreType) => state.accountPackage.accountPackageList)
    const systemPackageSettings = useSelector((state: StoreType) => state.system.packageSettings)

    const accountPermissions = useSelector((state: StoreType) => state.accountPermissions.accountPermissions)
    const projectPermissions = useSelector((state: StoreType) => state.accountPermissions.baseProjectPermissions)
    const reloadList = useSelector((state: StoreType) => state.accountPermissions.reloadList)

    const defaultGroupsAssigned = useSelector((state: StoreType) => state.system.accountSettings.project.defaultGroupsAssigned)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeTab, setActiveTab] = useState(menu.includes(hash) ? hash : ACCOUNT)
    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    const targetAccountId = accountId || userData.accountId

    // define package settings
    let packageSettings
    if (accountId) { // from account list
        const accountPackages = accountPackageList ? accountPackageList[accountId] : []
        const packageId = accountPackages && accountPackages.length ? accountPackages[0].packageId : null
        packageSettings = packageList[packageId] ? packageList[packageId].settingsParsed : initPackageSettings
    } else { // from user account
        packageSettings = systemPackageSettings
    }

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        if (!reloadList) return
        setLoadState(PENDING)
        Promise.all([
            LoadAccountPermissions(targetAccountId, lang).then((res) => {
                dispatch(setAccountPermissions(res.accountPermissionsSectioned, res.projectPermissionsSectioned))
            }),
            LoadAccountUsers(targetAccountId).then((accountUsers) => {
                const newAccountUserList: AccountUserList = { [targetAccountId]: accountUsers }
                dispatch(setAccountUserList(newAccountUserList))
            }),
            LoadAccountGroups(targetAccountId).then((res) => {
                dispatch(setGroupList(res))
            }),
            LoadProjectGroups(targetAccountId, null, defaultGroupsAssigned).then((res) => {
                dispatch(setProjectGroupList(res))
            }),
        ]).then(() => {
            setLoadState(IDLE)
        })
    }, [reloadList])

    const handleMenuClick = (tab: string) => {
        setActiveTab(tab)
        replaceHistoryState(`${mtPermissionsRoute}${tab}`)
    }

    const onCancel = () => {
        dispatch(setAccountPermissionsReloadList(false))
        setTimeout(() => {
            dispatch(setAccountPermissionsReloadList(true))
        }, 0)
    }

    const onApply = () => {
        setApplyState(PENDING)
        SaveAccountPermissions(targetAccountId, accountPermissions, projectPermissions)
        .then(() => {
            if (isMounted) setApplyState(IDLE)
        })
    }

    const listAccess = hasAccess(PRM_PERMISSIONS, LIST)
    const editAccess = hasAccess(PRM_PERMISSIONS, EDIT)

    const buttons = (
        <PageForm>
            <Row>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onApply()}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onCancel}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )

    if (!listAccess) return <AccessDenied />
    if (accountId) { // from Account View page
        return (
            <Container>
                <PageContent>
                    {loadState === PENDING && <Loader />}
                    <AccountPermissions
                        accountId={targetAccountId}
                        packageSettings={packageSettings}
                    />
                    {editAccess && buttons}
                </PageContent>
            </Container>
        )
    }
    return (
        <Container>
            <PageHeader>
                <PageTitle>{lang.permissions}</PageTitle>
            </PageHeader>
            <PageContent>
                <PageMenu>
                    <PageMenuButton
                        isActive={activeTab === ACCOUNT}
                        onClick={() => handleMenuClick(ACCOUNT)}
                    >
                        {lang.account}
                    </PageMenuButton>
                    <PageMenuButton
                        isActive={activeTab === PROJECT}
                        onClick={() => handleMenuClick(PROJECT)}
                    >
                        {lang.project}
                    </PageMenuButton>
                </PageMenu>
                <Tab show={activeTab === ACCOUNT}>
                    {loadState === PENDING && <Loader />}
                    <AccountPermissions
                        accountId={targetAccountId}
                        packageSettings={packageSettings}
                    />
                </Tab>
                <Tab show={activeTab === PROJECT}>
                    {loadState === PENDING && <Loader />}
                    <ProjectPermissions
                        accountId={targetAccountId}
                        packageSettings={packageSettings}
                    />
                </Tab>
                {editAccess && buttons}
            </PageContent>
        </Container>
    )
}
export default MtPermissions
